/**
 * 財布：アイテム「おはし」の所持数と、買い切り（広告除去）の購入状態。
 *
 * **県をまたいで共通。** 県別の記録（StorageSystem）とは別のキーに置く。
 * ここに入るのはアプリ版（と staging のテスト用モック）だけで、Web版では使わない。
 */

import { metaStorageKey } from '../game/config/gameConfig';
import type { StorageLike } from '../game/systems/StorageSystem';
import { JsonStore } from './jsonStore';

export interface WalletData {
  /** おはし（料理を1つつまんで消す）の所持数。 */
  items: number;
  /** 買い切り（広告除去）を持っているか。 */
  adFree: boolean;
  /** 買い切り特典「毎日1本」を最後に受け取った日（YYYY-MM-DD、端末の現地時間）。 */
  dailyClaimedOn: string;
  /** はじめての人に配る3本を配ったか。 */
  welcomeGiven: boolean;
  /** 前回のインタースティシャル広告から数えたゲームオーバー回数。 */
  gamesSinceInterstitial: number;
  /** 前回インタースティシャルを出した時刻（ms）。 */
  lastInterstitialAt: number;
  /**
   * 前回の広告（合間・ごほうび）から、実際に遊んだ時間の合計（ms）。
   * 2026-10-13：アプリを閉じても消えないよう、ここ（端末の保存領域）に持つ。
   */
  playMsSinceAd: number;
  /** 最後にアプリを使っていた時刻（ms）。起動直後かどうか（県の移動か、開き直しか）の判定に使う。 */
  lastActiveAt: number;
  /** トラッキング許可（ATT）を一度聞きにいったか。2026-10-20 から使っていない（起動直後に聞くため）。古い保存データのために残す。 */
  trackingAsked: boolean;
  /** 「今日」の日付と、その日に遊んだ回数・広告でもらったおはしの本数。 */
  today: string;
  gamesToday: number;
  adItemsToday: number;
}

/** インタースティシャルの出し方。 */
export interface InterstitialRule {
  /** 何回のゲームオーバーごとに出すか。 */
  every: number;
  /** 前回の広告（合間・ごほうび）から、または起動から、最低これだけ時間をあける（ms）。 */
  minIntervalMs: number;
  /** 前回の広告から、実際に遊んだ時間がこれだけたまったら出す（ms）。指定がなければ見ない。 */
  minPlayMs?: number;
  /** その日の最初の何回は出さない。 */
  freeGamesPerDay: number;
}

/** 所持数の上限。バグや連打で膨らんでも表示が壊れないように。 */
export const MAX_ITEMS = 999;
/** はじめての人に配る本数。 */
export const WELCOME_ITEMS = 3;

function count(value: unknown): number {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? Math.min(MAX_ITEMS, Math.floor(num)) : 0;
}

/** 0以上の時間・時刻（ms）。 */
function msValue(value: unknown): number {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

export function normalizeWallet(raw: unknown): WalletData {
  const r = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  return {
    items: count(r.items),
    adFree: r.adFree === true,
    dailyClaimedOn: typeof r.dailyClaimedOn === 'string' ? r.dailyClaimedOn : '',
    welcomeGiven: r.welcomeGiven === true,
    gamesSinceInterstitial: count(r.gamesSinceInterstitial),
    lastInterstitialAt: Number.isFinite(Number(r.lastInterstitialAt)) ? Number(r.lastInterstitialAt) : 0,
    playMsSinceAd: msValue(r.playMsSinceAd),
    lastActiveAt: msValue(r.lastActiveAt),
    trackingAsked: r.trackingAsked === true,
    today: typeof r.today === 'string' ? r.today : '',
    gamesToday: count(r.gamesToday),
    adItemsToday: count(r.adItemsToday),
  };
}

/** 端末の現地時間での日付（YYYY-MM-DD）。 */
export function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export class Wallet {
  private readonly store: JsonStore<WalletData>;

  constructor(storage?: StorageLike | null) {
    this.store = new JsonStore(metaStorageKey('wallet'), normalizeWallet, storage);
  }

  get items(): number {
    return this.store.data.items;
  }

  get adFree(): boolean {
    return this.store.data.adFree;
  }

  add(amount: number): number {
    return this.store.update((d) => {
      d.items = Math.min(MAX_ITEMS, d.items + Math.max(0, Math.floor(amount)));
    }).items;
  }

  /** 1本使う。持っていなければ false。 */
  consume(): boolean {
    if (this.store.data.items <= 0) return false;
    this.store.update((d) => {
      d.items -= 1;
    });
    return true;
  }

  setAdFree(value: boolean): void {
    this.store.update((d) => {
      d.adFree = value;
    });
  }

  /** はじめての人に1回だけ配る。配った本数を返す（2回目以降は 0）。 */
  grantWelcome(): number {
    if (this.store.data.welcomeGiven) return 0;
    this.store.update((d) => {
      d.welcomeGiven = true;
      d.items = Math.min(MAX_ITEMS, d.items + WELCOME_ITEMS);
    });
    return WELCOME_ITEMS;
  }

  /** 買い切り特典：1日1本。受け取れたら true。 */
  claimDaily(now: Date = new Date()): boolean {
    if (!this.store.data.adFree) return false;
    const today = localDateKey(now);
    if (this.store.data.dailyClaimedOn === today) return false;
    this.store.update((d) => {
      d.dailyClaimedOn = today;
      d.items = Math.min(MAX_ITEMS, d.items + 1);
    });
    return true;
  }

  /** 日付が変わっていたら「今日」の数を0に戻す。 */
  private rollDay(now: Date): void {
    const key = localDateKey(now);
    if (this.store.data.today === key) return;
    this.store.update((d) => {
      d.today = key;
      d.gamesToday = 0;
      d.adItemsToday = 0;
    });
  }

  /** 今日、広告でもらったおはしの本数。 */
  adItemsToday(now: Date = new Date()): number {
    this.rollDay(now);
    return this.store.data.adItemsToday;
  }

  /** 広告でもらったおはしを1本数える。 */
  countAdItem(now: Date = new Date()): void {
    this.rollDay(now);
    this.store.update((d) => {
      d.adItemsToday += 1;
    });
  }

  /**
   * ゲームオーバーを1回数え、インタースティシャルを出す番なら true。
   * 買い切り済みなら常に false（数えもしない）。
   *
   * - その日の最初の数回は出さない（その日はじめて開いた人を広告で迎えない）
   * - every 回に1回
   * - 前回から minIntervalMs 以上たっていること
   */
  /** 合間の広告の数え方の控え（出せなかったときに戻すため）。 */
  interstitialSnapshot(): { gamesSinceInterstitial: number; lastInterstitialAt: number; playMsSinceAd: number } {
    const d = this.store.data;
    return {
      gamesSinceInterstitial: d.gamesSinceInterstitial,
      lastInterstitialAt: d.lastInterstitialAt,
      playMsSinceAd: d.playMsSinceAd,
    };
  }

  /**
   * 出す番だったのに合間の広告を出せなかった（読み込めていなかった）。
   * 「出した」記録を取り消し、次の「もう一度」でまた出す番にする（8分の時計を空振りで進めない）。
   */
  restoreInterstitial(snapshot: { gamesSinceInterstitial: number; lastInterstitialAt: number; playMsSinceAd: number }): void {
    this.store.update((d) => {
      d.gamesSinceInterstitial = snapshot.gamesSinceInterstitial;
      d.lastInterstitialAt = snapshot.lastInterstitialAt;
      d.playMsSinceAd = snapshot.playMsSinceAd;
    });
  }

  /** 遊んだ時間を足す（ゲーム中で画面が出ているあいだだけ、数秒ごとに呼ぶ）。 */
  addPlayTime(ms: number): void {
    if (!(ms > 0)) return;
    this.store.update((d) => {
      d.playMsSinceAd = Math.min(d.playMsSinceAd + ms, 24 * 60 * 60_000);
    });
  }

  get playMsSinceAd(): number {
    return this.store.data.playMsSinceAd;
  }

  /** アプリを使っている印（数秒ごと）。 */
  touchActive(now: number): void {
    this.store.update((d) => {
      d.lastActiveAt = now;
    });
  }

  get lastActiveAt(): number {
    return this.store.data.lastActiveAt;
  }

  get trackingAsked(): boolean {
    return this.store.data.trackingAsked;
  }

  setTrackingAsked(): void {
    if (this.store.data.trackingAsked) return;
    this.store.update((d) => {
      d.trackingAsked = true;
    });
  }

  /** ごほうび広告を見た。合間の広告の「前回の広告」として数える（続けて広告を見せない）。 */
  noteAdShown(now: Date = new Date()): void {
    this.store.update((d) => {
      d.lastInterstitialAt = now.getTime();
      d.playMsSinceAd = 0;
    });
  }

  /**
   * 「もう一度」を押したときに合間の広告を出す番か。出す番なら記録して true。
   * sinceAt：これより前からは数えない（起動した時刻。起動直後の短いプレイのあとに出さない）。
   */
  countGameForInterstitial(rule: InterstitialRule, now: Date = new Date(), sinceAt = 0): boolean {
    if (this.store.data.adFree) return false;
    this.rollDay(now);
    const gamesToday = this.store.data.gamesToday + 1;
    const next = this.store.data.gamesSinceInterstitial + 1;
    const playedEnough = rule.minPlayMs === undefined || this.store.data.playMsSinceAd >= rule.minPlayMs;
    const due =
      gamesToday > rule.freeGamesPerDay &&
      next >= rule.every &&
      playedEnough &&
      now.getTime() - Math.max(this.store.data.lastInterstitialAt, sinceAt) >= rule.minIntervalMs;
    this.store.update((d) => {
      d.gamesToday = gamesToday;
      d.gamesSinceInterstitial = due ? 0 : next;
      if (due) {
        d.lastInterstitialAt = now.getTime();
        d.playMsSinceAd = 0;
      }
    });
    return due;
  }
}
